import React, { useState, useRef, useEffect, useCallback, useLayoutEffect } from 'react';
import DOMPurify from 'dompurify';
import { marked } from 'marked';
import { useAuth } from '../contexts/AuthContext';
import { Send, Copy, Clock, Check } from 'lucide-react';
import '../styles/jason.css';

// Configure marked once
marked.setOptions({ breaks: true, gfm: true });

function JasonV3() {
  const { user, isAuthenticated } = useAuth();

  // Chat state
  const [chat, setChat] = useState([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [welcomeContext, setWelcomeContext] = useState(null);
  const [welcomeLoading, setWelcomeLoading] = useState(false);
  const [imageModal, setImageModal] = useState({ show: false, url: '' });
  const [showHistory, setShowHistory] = useState(false);
  const [currentChatId, setCurrentChatId] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  // Chat history from backend
  const [chatHistory, setChatHistory] = useState([]);

  const messagesRef = useRef(null);
  const textareaRef = useRef(null);

  const renderMarkdown = useCallback((content) => {
    if (!content) return '';
    const md = content.replace(/\\n/g, '\n');
    return DOMPurify.sanitize(marked.parse(md));
  }, []);

  // Determine if we are in the "welcome" state (no user messages sent yet)
  const hasConversation = chat.some(m => m.type === 'user');

  // Fetch contextual welcome message from n8n
  const fetchWelcome = useCallback(async () => {
    setWelcomeLoading(true);
    try {
      const result = await fetch('https://n8n.iniai.cloud/webhook/jason_v3', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'x-api-key': process.env.REACT_APP_CREATE_USER_API_KEY,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          type: 'welcome',
          user: {
            id: user?.id || '',
            email: user?.email || '',
            role: user?.role || '',
            name: user?.name || ''
          },
          sessionId: `${user?.id || 'anonymous'}-jason-v3-session`,
          context: {
            isAuthenticated,
            timestamp: new Date().toISOString()
          }
        })
      });

      const data = await result.json();
      if (Array.isArray(data) && data.length > 0) {
        const response = data[0].output || data[0].response;
        if (response) {
          setWelcomeContext(response);
        }
      }
    } catch (error) {
      console.error('Error fetching welcome message:', error);
    } finally {
      setWelcomeLoading(false);
    }
  }, [user, isAuthenticated]);

  // Fetch welcome on mount
  useEffect(() => {
    if (isAuthenticated && user && chat.length === 0) {
      fetchWelcome();
    }
  }, [isAuthenticated, user, chat.length, fetchWelcome]);

  // Auto-scroll to bottom
  useLayoutEffect(() => {
    if (messagesRef.current) {
      requestAnimationFrame(() => {
        messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
      });
    }
  }, [chat, loading]);

  // Fetch chat history from backend
  const fetchChatHistory = useCallback(async () => {
    try {
      const result = await fetch('https://n8n.iniai.cloud/webhook/jason-chat-history', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'x-api-key': process.env.REACT_APP_CREATE_USER_API_KEY,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ userId: user?.id || 'matthew' })
      });
      const data = await result.json();
      console.log('Chat history raw response:', data);
      const convos = Array.isArray(data) ? data[0]?.conversations : data?.conversations;
      console.log('Parsed conversations:', convos?.length, 'items');
      if (convos && convos.length > 0) {
        setChatHistory(convos.map(c => ({
          id: c.id,
          title: c.title,
          lastMessage: c.preview || '',
          messageCount: c.messages?.length || 0,
          messages: c.messages || []
        })));
      }
    } catch (err) {
      console.error('Error fetching chat history:', err);
    }
  }, [user]);

  useEffect(() => {
    if (isAuthenticated && user) {
      fetchChatHistory();
    }
  }, [isAuthenticated, user, fetchChatHistory]);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!query.trim() || loading) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: query.trim(),
      timestamp: new Date()
    };

    setChat(prevChat => [...prevChat, userMessage]);
    const currentQuery = query;
    setQuery('');
    setLoading(true);

    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 600000);

    try {
      const result = await fetch('https://n8n.iniai.cloud/webhook/jason_v3', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': process.env.REACT_APP_CREATE_USER_API_KEY,
        },
        signal: controller.signal,
        body: JSON.stringify({
          type: 'query',
          query: currentQuery,
          user: {
            id: user?.id || '',
            email: user?.email || '',
            role: user?.role || '',
            name: user?.name || ''
          },
          sessionId: `${user?.id || 'anonymous'}-jason-v3-session`,
          context: {
            isAuthenticated,
            timestamp: new Date().toISOString()
          }
        })
      });

      clearTimeout(timeoutId);
      const data = await result.json();

      if (Array.isArray(data) && data.length > 0) {
        const rawContent = data[0].response || data[0].output;
        const assistantMessage = {
          id: Date.now() + 1,
          type: 'jason',
          content: rawContent,
          timestamp: new Date()
        };
        setChat(prevChat => [...prevChat, assistantMessage]);
        saveChatToHistory();
      }
    } catch (error) {
      clearTimeout(timeoutId);

      const errorText = error.name === 'AbortError'
        ? 'This query is taking longer than expected. Try simplifying your question.'
        : 'An error occurred while processing your request.';

      setChat(prevChat => [...prevChat, {
        id: Date.now() + 1,
        type: 'error',
        content: errorText,
        timestamp: new Date()
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleTextareaChange = (e) => {
    setQuery(e.target.value);
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = Math.min(textarea.scrollHeight, 140) + 'px';
    }
  };

  const copyToClipboard = useCallback((messageId, content) => {
    const cleanText = content.replace(/<[^>]*>?/gm, '');
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(cleanText).then(() => {
        setCopiedId(messageId);
        setTimeout(() => setCopiedId(null), 1500);
      }).catch(() => {});
    } else {
      const textarea = document.createElement('textarea');
      textarea.value = cleanText;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      try {
        document.execCommand('copy');
        setCopiedId(messageId);
        setTimeout(() => setCopiedId(null), 1500);
      } catch (err) {
        // silent fail
      }
      document.body.removeChild(textarea);
    }
  }, []);

  const openImageModal = useCallback((imageUrl) => {
    setImageModal({ show: true, url: imageUrl });
  }, []);

  const closeImageModal = () => {
    setImageModal({ show: false, url: '' });
  };

  // Make openImageModal available for inline onclick in markdown images
  useEffect(() => {
    window.openImageModal = openImageModal;
    return () => { delete window.openImageModal; };
  }, [openImageModal]);


  const saveChatToHistory = () => {
    // Refresh history from backend after new messages are saved
    fetchChatHistory();
  };

  const startNewChat = () => {
    setChat([]);
    setCurrentChatId(null);
    setShowHistory(false);
    setWelcomeContext(null);
    fetchWelcome();
  };

  const loadChat = (chatId) => {
    const savedChat = chatHistory.find(c => c.id === chatId);
    if (savedChat) {
      setChat(savedChat.messages);
      setCurrentChatId(chatId);
      setShowHistory(false);
    }
  };

  // --- Render ---

  // Welcome state: centred layout with name, divider, optional context, and input
  if (!hasConversation && !loading) {
    return (
      <div className="jason-workspace">
        {/* Header */}
        <div className="jason-header">
          <div className="jason-header-left">
            <span className="jason-header-title">Jason</span>
          </div>
          <div className="jason-header-actions">
            <button
              className="jason-header-btn"
              onClick={() => setShowHistory(true)}
            >
              History
            </button>
            <button
              className="jason-header-btn"
              onClick={startNewChat}
            >
              New
            </button>
          </div>
        </div>

        {/* Centred welcome */}
        <div className="jason-welcome">
          <h1 className="jason-welcome-name">Jason</h1>
          <hr className="jason-welcome-divider" />
          <p className="jason-welcome-prompt">What are you working through today?</p>

          {welcomeLoading && (
            <div className="jason-welcome-context">
              <span className="jason-thinking-text">Thinking</span>
            </div>
          )}

          {!welcomeLoading && welcomeContext && (
            <div
              className="jason-welcome-context"
              dangerouslySetInnerHTML={{ __html: renderMarkdown(welcomeContext) }}
            />
          )}

          <div className="jason-welcome-input-wrapper">
            <div className="jason-input-container">
              <textarea
                ref={textareaRef}
                value={query}
                onChange={handleTextareaChange}
                onKeyDown={handleKeyDown}
                placeholder="Ask Jason..."
                className="jason-textarea"
                rows={1}
              />
              <button
                type="button"
                onClick={handleSubmit}
                disabled={!query.trim()}
                className="jason-send-btn"
              >
                <Send size={16} />
              </button>
            </div>
          </div>
        </div>

        {/* History overlay */}
        {showHistory && (
          <div className="jason-history-overlay">
            <div className="jason-history-panel">
              <div className="jason-history-header">
                <h2>History</h2>
                <button
                  className="jason-history-close"
                  onClick={() => setShowHistory(false)}
                >
                  &times;
                </button>
              </div>
              <div className="jason-history-list">
                {chatHistory.length === 0 && (
                  <div className="jason-history-empty">No conversations yet</div>
                )}
                {chatHistory.map((saved) => (
                  <div
                    key={saved.id}
                    className={`jason-history-item ${currentChatId === saved.id ? 'active' : ''}`}
                    onClick={() => loadChat(saved.id)}
                  >
                    <div className="jason-history-item-title">{saved.title}</div>
                    <div className="jason-history-item-date">
                      <Clock size={11} />
                      <span>{saved.messageCount || 0} messages</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="jason-history-scrim" onClick={() => setShowHistory(false)} />
          </div>
        )}

        {/* Image modal */}
        {imageModal.show && (
          <div className="jason-image-modal" onClick={closeImageModal}>
            <img
              src={imageModal.url}
              alt="Full size"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        )}
      </div>
    );
  }

  // Conversation state: header, messages, input at bottom
  return (
    <div className="jason-workspace">
      {/* Header */}
      <div className="jason-header">
        <div className="jason-header-left">
          <span className="jason-header-title">Jason</span>
        </div>
        <div className="jason-header-actions">
          <button
            className="jason-header-btn"
            onClick={() => setShowHistory(true)}
          >
            History
          </button>
          <button
            className="jason-header-btn"
            onClick={startNewChat}
          >
            New
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="jason-messages-area" ref={messagesRef}>
        <div className="jason-messages-wrapper">
          {chat.map((message) => {
            if (message.type === 'user') {
              return (
                <div key={message.id} className="jason-exchange">
                  <div className="jason-user-message">
                    <div className="jason-user-message-content">
                      {message.content}
                    </div>
                  </div>
                </div>
              );
            }

            if (message.type === 'error') {
              return (
                <div key={message.id} className="jason-exchange">
                  <div className="jason-error">{message.content}</div>
                </div>
              );
            }

            // Jason response — document style
            return (
              <div key={message.id} className="jason-exchange">
                <div className="jason-response">
                  <div
                    className="jason-response-content"
                    dangerouslySetInnerHTML={{ __html: renderMarkdown(message.content) }}
                  />
                  <div className="jason-response-actions">
                    <button
                      className="jason-action-btn"
                      onClick={() => copyToClipboard(message.id, message.content)}
                      title="Copy"
                    >
                      {copiedId === message.id ? <Check size={14} /> : <Copy size={14} />}
                    </button>
                    <span className={`jason-copy-tooltip ${copiedId === message.id ? 'visible' : ''}`}>
                      Copied
                    </span>
                  </div>
                </div>
              </div>
            );
          })}

          {/* Thinking indicator */}
          {loading && (
            <div className="jason-thinking">
              <span className="jason-thinking-text">Thinking</span>
            </div>
          )}
        </div>
      </div>

      {/* Input area */}
      <div className="jason-input-area">
        <div className="jason-input-wrapper">
          <div className="jason-input-container">
            <textarea
              ref={textareaRef}
              value={query}
              onChange={handleTextareaChange}
              onKeyDown={handleKeyDown}
              placeholder="Ask Jason..."
              className="jason-textarea"
              rows={1}
              disabled={loading}
            />
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!query.trim() || loading}
              className="jason-send-btn"
            >
              <Send size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* History overlay */}
      {showHistory && (
        <div className="jason-history-overlay">
          <div className="jason-history-panel">
            <div className="jason-history-header">
              <h2>History</h2>
              <button
                className="jason-history-close"
                onClick={() => setShowHistory(false)}
              >
                &times;
              </button>
            </div>
            <div className="jason-history-list">
              {chatHistory.length === 0 && (
                <div className="jason-history-empty">No conversations yet</div>
              )}
              {chatHistory.map((saved) => (
                <div
                  key={saved.id}
                  className={`jason-history-item ${currentChatId === saved.id ? 'active' : ''}`}
                  onClick={() => loadChat(saved.id)}
                >
                  <div className="jason-history-item-title">{saved.title}</div>
                  <div className="jason-history-item-date">
                    <Clock size={11} />
                    <span>{saved.messageCount || 0} messages</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="jason-history-scrim" onClick={() => setShowHistory(false)} />
        </div>
      )}

      {/* Image modal */}
      {imageModal.show && (
        <div className="jason-image-modal" onClick={closeImageModal}>
          <img
            src={imageModal.url}
            alt="Full size"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}

export default JasonV3;
